import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, getDocs, query, collection, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { ladeSpielerNamen, spielerName } from '../utils/spielerNamen';
import { useAuth } from '../contexts/AuthContext';
import TeamAuswahl from '../components/TeamAuswahl';
import { NFL_TEAMS, DIVISIONS, getTeamsByConference } from '../data/nflTeams';
import { saisonIdZuName } from '../utils/saison';
import { istPlayoffSpiel } from '../utils/saisonPhasen';
import './BonusFragen.css';

// Bonusfragen: Pre-Season- und Playoff-Predictions. Antwort-Doc und
// Feldnamen bleiben exakt wie bisher (die Admin-Auswertung hängt daran):
//   bonusFragenAntworten/{uid}_{saisonId}
//   { userId, saison, preSeasonPredictions: {...}, playoffPredictions: {...} }
// Deadlines: Pre-Season bis zum ersten Spiel der Saison, Playoffs bis zum
// ersten Playoff-Spiel – aus je einer kleinen Abfrage statt aller Spiele.

const LEGACY_PLAYOFF_WOCHEN = [19, 20, 21, 22];

const formatZeit = (iso) =>
  new Date(iso).toLocaleString('de-DE', {
    weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
  });

const divKey = (divisionName) => `division_${divisionName.replace(/\s/g, '_')}`;

function BonusFragen() {
  const { saisonId } = useParams();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  const saisonName = saisonIdZuName(saisonId);

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);           // saisonKonfiguration.bonusfragen
  const [saisonGesperrt, setSaisonGesperrt] = useState(false);
  const [deadlines, setDeadlines] = useState({ preSeason: null, playoff: null, aktiv: true });
  const [antworten, setAntworten] = useState({ preSeasonPredictions: {}, playoffPredictions: {} });
  const [gespeichert, setGespeichert] = useState({ preSeasonPredictions: {}, playoffPredictions: {} });
  const [punkte, setPunkte] = useState(null);           // punkte.bonusfragen
  const [speichernLaeuft, setSpeichernLaeuft] = useState(false);

  const [roster, setRoster] = useState([]);
  const [rosterLaedt, setRosterLaedt] = useState(false);

  const [zeigeAndere, setZeigeAndere] = useState({ preSeason: false, playoff: false });
  const [andere, setAndere] = useState(null);
  const [andereLaden, setAndereLaden] = useState(false);

  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const zeigeToast = useCallback((text, fehler = false) => {
    setToast({ text, fehler });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  }, []);

  // ---------- Laden ----------
  useEffect(() => {
    if (!uid) return undefined;
    let abgebrochen = false;

    const lade = async () => {
      setLoading(true);
      setAndere(null);
      setZeigeAndere({ preSeason: false, playoff: false });
      try {
        const spiele = collection(db, 'spiele');
        const [configSnap, antwortSnap, punkteSnap, erstesSnap, playoffSnap] = await Promise.all([
          getDoc(doc(db, 'saisonKonfiguration', saisonId)),
          getDoc(doc(db, 'bonusFragenAntworten', `${uid}_${saisonId}`)),
          getDoc(doc(db, 'punkte', `${uid}_${saisonId}`)),
          getDocs(query(spiele, where('saison', '==', saisonName), orderBy('datum', 'asc'), limit(1))),
          getDocs(query(spiele, where('saison', '==', saisonName), where('seasonType', '==', 3)))
        ]);
        if (abgebrochen) return;

        let playoffDocs = playoffSnap.docs;
        if (playoffDocs.length === 0) {
          const legacy = await getDocs(query(spiele, where('saison', '==', saisonName), where('week', 'in', LEGACY_PLAYOFF_WOCHEN)));
          if (abgebrochen) return;
          playoffDocs = legacy.docs;
        }

        const c = configSnap.exists() ? configSnap.data() : {};
        setConfig(c.bonusfragen || null);
        setSaisonGesperrt(c.gesperrt || false);

        const erstesPlayoff = playoffDocs
          .map(d => d.data())
          .filter(s => istPlayoffSpiel(s))
          .map(s => s.datum)
          .sort()[0] || null;
        // Admin kann die Deadlines überschreiben (saisonKonfiguration.bonusfragenDeadlines)
        const override = c.bonusfragenDeadlines || {};
        setDeadlines({
          preSeason: override.preSeason || (erstesSnap.empty ? null : erstesSnap.docs[0].data().datum),
          playoff: override.playoff || erstesPlayoff,
          aktiv: c.deadlinesAktiv !== undefined ? c.deadlinesAktiv : true
        });

        const a = antwortSnap.exists() ? antwortSnap.data() : {};
        const geladen = {
          preSeasonPredictions: a.preSeasonPredictions || {},
          playoffPredictions: a.playoffPredictions || {}
        };
        setAntworten(geladen);
        setGespeichert(geladen);

        setPunkte(punkteSnap.exists() ? (punkteSnap.data().bonusfragen || null) : null);
      } catch (error) {
        console.error('Fehler beim Laden der Bonusfragen:', error);
        zeigeToast('Laden fehlgeschlagen', true);
      } finally {
        if (!abgebrochen) setLoading(false);
      }
    };

    lade();
    return () => { abgebrochen = true; };
  }, [uid, saisonId, saisonName, zeigeToast]);

  // MVP-Roster nachladen, wenn ein Team gespeichert ist
  const mvpTeam = antworten.preSeasonPredictions.mvpTeam;
  useEffect(() => {
    if (!mvpTeam) { setRoster([]); return undefined; }
    let abgebrochen = false;
    const lade = async () => {
      setRosterLaedt(true);
      try {
        const espnId = NFL_TEAMS[mvpTeam]?.espnId;
        const res = await fetch(`https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams/${espnId}/roster`);
        if (!res.ok) throw new Error('Roster nicht erreichbar');
        const data = await res.json();
        const athletes = [];
        (Array.isArray(data.athletes) ? data.athletes : []).forEach(group => {
          if (Array.isArray(group.items)) athletes.push(...group.items);
        });
        const liste = athletes
          .map(a => ({
            id: a.id || a.displayName,
            name: a.displayName || a.fullName || '',
            position: a.position?.abbreviation || a.position?.name || '',
            number: a.jersey || '-'
          }))
          .filter(p => p.name)
          .sort((a, b) => (parseInt(a.number, 10) || 999) - (parseInt(b.number, 10) || 999));
        if (!abgebrochen) setRoster(liste);
      } catch (error) {
        console.error('Roster-Fehler:', error);
        if (!abgebrochen) setRoster([]);
      } finally {
        if (!abgebrochen) setRosterLaedt(false);
      }
    };
    lade();
    return () => { abgebrochen = true; };
  }, [mvpTeam]);

  // Tipps der anderen (beim ersten Einblenden)
  const brauchtAndere = zeigeAndere.preSeason || zeigeAndere.playoff;
  useEffect(() => {
    if (!brauchtAndere || andere !== null || !uid) return undefined;
    let abgebrochen = false;
    const lade = async () => {
      setAndereLaden(true);
      try {
        const [antwortenSnap, namen] = await Promise.all([
          getDocs(query(collection(db, 'bonusFragenAntworten'), where('saison', '==', saisonId))),
          ladeSpielerNamen()
        ]);
        if (abgebrochen) return;
        setAndere(antwortenSnap.docs
          .map(d => ({ userId: d.data().userId, name: spielerName(namen, d.data().userId), pre: d.data().preSeasonPredictions || {}, playoff: d.data().playoffPredictions || {} }))
          .filter(x => x.userId && x.userId !== uid)
          .sort((a, b) => a.name.localeCompare(b.name, 'de')));
      } catch (error) {
        console.error('Fehler beim Laden der anderen Predictions:', error);
        setAndere([]);
      } finally {
        if (!abgebrochen) setAndereLaden(false);
      }
    };
    lade();
    return () => { abgebrochen = true; };
  }, [brauchtAndere, andere, uid, saisonId]);

  // ---------- Ableitungen ----------
  const jetzt = new Date();
  const istVorbei = (iso) => Boolean(iso) && new Date(iso) <= jetzt;
  const gesperrt = {
    preSeason: saisonGesperrt || (deadlines.aktiv && istVorbei(deadlines.preSeason)),
    playoff: saisonGesperrt || (deadlines.aktiv && istVorbei(deadlines.playoff))
  };

  const geaendert = useMemo(() => ({
    preSeason: JSON.stringify(antworten.preSeasonPredictions) !== JSON.stringify(gespeichert.preSeasonPredictions),
    playoff: JSON.stringify(antworten.playoffPredictions) !== JSON.stringify(gespeichert.playoffPredictions)
  }), [antworten, gespeichert]);
  const hatAenderungen = (geaendert.preSeason && !gesperrt.preSeason) || (geaendert.playoff && !gesperrt.playoff);

  const setPre = (feld, wert) => setAntworten(a => ({ ...a, preSeasonPredictions: { ...a.preSeasonPredictions, [feld]: wert } }));
  const setPlayoff = (feld, wert) => setAntworten(a => ({ ...a, playoffPredictions: { ...a.playoffPredictions, [feld]: wert } }));

  const handleSpeichern = async () => {
    if (!uid || !hatAenderungen) return;
    setSpeichernLaeuft(true);
    try {
      const daten = { userId: uid, saison: saisonId, gespeichertAm: new Date().toISOString() };
      if (!gesperrt.preSeason) daten.preSeasonPredictions = antworten.preSeasonPredictions;
      if (!gesperrt.playoff) daten.playoffPredictions = antworten.playoffPredictions;
      await setDoc(doc(db, 'bonusFragenAntworten', `${uid}_${saisonId}`), daten, { merge: true });
      setGespeichert({
        preSeasonPredictions: gesperrt.preSeason ? gespeichert.preSeasonPredictions : antworten.preSeasonPredictions,
        playoffPredictions: gesperrt.playoff ? gespeichert.playoffPredictions : antworten.playoffPredictions
      });
      zeigeToast('✓ Predictions gespeichert');
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      zeigeToast('Speichern fehlgeschlagen: ' + error.message, true);
    } finally {
      setSpeichernLaeuft(false);
    }
  };

  const pre = antworten.preSeasonPredictions;
  const po = antworten.playoffPredictions;
  const AFC = getTeamsByConference('AFC');
  const NFC = getTeamsByConference('NFC');

  const ergebnisPre = punkte?.details?.preSeason || {};
  const ergebnisPo = punkte?.details?.playoff || {};

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="bonus-seite">
        <h1>🎯 Predictions {saisonName}</h1>
        <div className="bonus-block">⏳ Lade Bonusfragen…</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="bonus-seite">
        <h1>🎯 Predictions {saisonName}</h1>
        <div className="bonus-block">Für diese Saison sind keine Bonusfragen konfiguriert.</div>
      </div>
    );
  }

  const preCfg = config.preSeasonPredictions || {};
  const poCfg = config.playoffPredictions || {};

  return (
    <div className="bonus-seite">
      <h1>🎯 Predictions {saisonName}</h1>

      {saisonGesperrt && (
        <div className="bonus-block bonus-block--gesperrt">
          <strong>🔒 Saison abgeschlossen.</strong> Die Predictions sind nur noch zum Ansehen da.
        </div>
      )}

      {/* ================= PRE-SEASON ================= */}
      <section className={`bonus-block${gesperrt.preSeason ? ' bonus-block--gesperrt' : ''}`}>
        <div className="bonus-block__kopf">
          <h2>📅 Pre-Season</h2>
          {punkte && <span className="bonus-block__punkte">{(punkte.preSeason || 0)} P</span>}
          <StatusChip gesperrt={gesperrt.preSeason} deadline={deadlines.preSeason} aktiv={deadlines.aktiv} />
        </div>
        <p className="bonus-block__hinweis">
          Tipps auf die ganze Saison. {deadlines.aktiv && deadlines.preSeason && !gesperrt.preSeason && `Deadline: ${formatZeit(deadlines.preSeason)}.`}
        </p>
        {gesperrt.preSeason && (
          <AndereToggle aktiv={zeigeAndere.preSeason} onToggle={() => setZeigeAndere(z => ({ ...z, preSeason: !z.preSeason }))} />
        )}

        {preCfg.divisionWinners?.aktiv && (
          <Frage titel="🏆 Division Winners" punkte={`${preCfg.divisionWinners.punkte} P je Division`} ergebnis={ergebnisPre.divisionWinners}>
            <div className="frage__grid frage__grid--4">
              {Object.entries(DIVISIONS).map(([name, teams]) => (
                <TeamAuswahl key={name} label={name} value={pre[divKey(name)]} teams={teams} disabled={gesperrt.preSeason}
                  onChange={v => setPre(divKey(name), v)} />
              ))}
            </div>
            {zeigeAndere.preSeason && (
              <AndereListe andere={andere} laden={andereLaden} render={a =>
                Object.keys(DIVISIONS).map(n => a.pre[divKey(n)]).filter(Boolean).join(', ')} />
            )}
          </Frage>
        )}

        {preCfg.conferenceSeed1?.aktiv && (
          <Frage titel="1️⃣ Conference #1 Seeds" punkte={`${preCfg.conferenceSeed1.punkte} P je Conference`} ergebnis={ergebnisPre.conferenceSeed1}>
            <div className="frage__grid frage__grid--2">
              <TeamAuswahl label="AFC #1 Seed" value={pre.afcSeed1} teams={AFC} disabled={gesperrt.preSeason} onChange={v => setPre('afcSeed1', v)} />
              <TeamAuswahl label="NFC #1 Seed" value={pre.nfcSeed1} teams={NFC} disabled={gesperrt.preSeason} onChange={v => setPre('nfcSeed1', v)} />
            </div>
            {zeigeAndere.preSeason && (
              <AndereListe andere={andere} laden={andereLaden} render={a => [a.pre.afcSeed1, a.pre.nfcSeed1].filter(Boolean).join(' / ')} />
            )}
          </Frage>
        )}

        {preCfg.wildCardTeams?.aktiv && (
          <Frage titel="🃏 Wild Card Teams" punkte={`${preCfg.wildCardTeams.punkte} P je Team`} ergebnis={ergebnisPre.wildCardTeams}>
            <div className="frage__untertitel">AFC (3 Teams)</div>
            <div className="frage__grid frage__grid--3">
              {[1, 2, 3].map(n => (
                <TeamAuswahl key={`afc${n}`} label={`AFC Wild Card ${n}`} value={pre[`afcWildCard${n}`]} teams={AFC} disabled={gesperrt.preSeason}
                  ausschliessen={[1, 2, 3].filter(m => m !== n).map(m => pre[`afcWildCard${m}`]).filter(Boolean)}
                  onChange={v => setPre(`afcWildCard${n}`, v)} />
              ))}
            </div>
            <div className="frage__untertitel">NFC (3 Teams)</div>
            <div className="frage__grid frage__grid--3">
              {[1, 2, 3].map(n => (
                <TeamAuswahl key={`nfc${n}`} label={`NFC Wild Card ${n}`} value={pre[`nfcWildCard${n}`]} teams={NFC} disabled={gesperrt.preSeason}
                  ausschliessen={[1, 2, 3].filter(m => m !== n).map(m => pre[`nfcWildCard${m}`]).filter(Boolean)}
                  onChange={v => setPre(`nfcWildCard${n}`, v)} />
              ))}
            </div>
            {zeigeAndere.preSeason && (
              <AndereListe andere={andere} laden={andereLaden} render={a =>
                [1, 2, 3].map(n => a.pre[`afcWildCard${n}`]).filter(Boolean).join(', ') + ' / ' +
                [1, 2, 3].map(n => a.pre[`nfcWildCard${n}`]).filter(Boolean).join(', ')} />
            )}
          </Frage>
        )}

        {preCfg.superBowlWinner?.aktiv && (
          <Frage titel="🏆 Super Bowl Champion" punkte={`${preCfg.superBowlWinner.punkte} P`} ergebnis={ergebnisPre.superBowlWinner}>
            <div className="frage__grid frage__grid--2">
              <TeamAuswahl label="Wer gewinnt den Super Bowl?" value={pre.superBowlWinner} teams={Object.keys(NFL_TEAMS)} disabled={gesperrt.preSeason}
                onChange={v => setPre('superBowlWinner', v)} />
            </div>
            {zeigeAndere.preSeason && <AndereListe andere={andere} laden={andereLaden} logo render={a => a.pre.superBowlWinner} />}
          </Frage>
        )}

        {preCfg.mvp?.aktiv && (
          <Frage titel="⭐ MVP" punkte={`${preCfg.mvp.punkte} P`} ergebnis={ergebnisPre.mvp}>
            <div className="frage__grid frage__grid--2">
              <TeamAuswahl label="1. Team des MVP" value={pre.mvpTeam} teams={Object.keys(NFL_TEAMS)} disabled={gesperrt.preSeason}
                onChange={v => setAntworten(a => ({
                  ...a,
                  preSeasonPredictions: { ...a.preSeasonPredictions, mvpTeam: v, mvpPlayer: '', mvpPlayerName: '', mvpPlayerNumber: '', mvpPlayerPosition: '' }
                }))} />
              <div>
                <span className="team-auswahl__label">2. Spieler</span>
                {!pre.mvpTeam ? (
                  <div className="andere-pick--leer">Erst ein Team wählen.</div>
                ) : rosterLaedt ? (
                  <div className="andere-pick--leer">⏳ Lade Kader…</div>
                ) : roster.length > 0 ? (
                  <select
                    className="spieler-select"
                    value={pre.mvpPlayer || ''}
                    disabled={gesperrt.preSeason}
                    onChange={e => {
                      const p = roster.find(r => r.id === e.target.value);
                      setAntworten(a => ({
                        ...a,
                        preSeasonPredictions: {
                          ...a.preSeasonPredictions,
                          mvpPlayer: e.target.value,
                          mvpPlayerName: p?.name || '',
                          mvpPlayerNumber: p?.number || '',
                          mvpPlayerPosition: p?.position || ''
                        }
                      }));
                    }}
                  >
                    <option value="">-- Spieler wählen --</option>
                    {roster.map(p => (
                      <option key={p.id} value={p.id}>#{p.number} {p.name} ({p.position})</option>
                    ))}
                  </select>
                ) : pre.mvpPlayerName ? (
                  <div className="andere-pick--leer">Kader gerade nicht abrufbar – deine Auswahl bleibt gespeichert.</div>
                ) : (
                  <div className="andere-pick--leer">Kader konnte nicht geladen werden.</div>
                )}
              </div>
            </div>
            {pre.mvpPlayerName && pre.mvpTeam && (
              <div className="mvp-karte">
                <img src={NFL_TEAMS[pre.mvpTeam]?.logo} alt="" />
                <div>
                  <strong>{pre.mvpPlayerName}</strong>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {NFL_TEAMS[pre.mvpTeam]?.name} · #{pre.mvpPlayerNumber} · {pre.mvpPlayerPosition}
                  </div>
                </div>
              </div>
            )}
            {zeigeAndere.preSeason && (
              <AndereListe andere={andere} laden={andereLaden} render={a => a.pre.mvpPlayerName ? `${a.pre.mvpPlayerName} (${a.pre.mvpTeam})` : ''} />
            )}
          </Frage>
        )}
      </section>

      {/* ================= PLAYOFFS ================= */}
      <section className={`bonus-block${gesperrt.playoff ? ' bonus-block--gesperrt' : ''}`}>
        <div className="bonus-block__kopf">
          <h2>🏆 Playoffs</h2>
          {punkte && <span className="bonus-block__punkte">{(punkte.playoff || 0)} P</span>}
          <StatusChip gesperrt={gesperrt.playoff} deadline={deadlines.playoff} aktiv={deadlines.aktiv} />
        </div>
        <p className="bonus-block__hinweis">
          {deadlines.playoff
            ? (deadlines.aktiv && !gesperrt.playoff ? `Tipps auf die Playoffs. Deadline: ${formatZeit(deadlines.playoff)}.` : 'Tipps auf die Playoffs.')
            : 'Tipps auf die Playoffs. Die Deadline steht fest, sobald die Playoff-Paarungen importiert sind.'}
        </p>
        {gesperrt.playoff && (
          <AndereToggle aktiv={zeigeAndere.playoff} onToggle={() => setZeigeAndere(z => ({ ...z, playoff: !z.playoff }))} />
        )}

        {poCfg.conferenceChampionshipTeams?.aktiv && (
          <Frage titel="🏈 Conference Championship Teams" punkte={`${poCfg.conferenceChampionshipTeams.punkte} P je Team`} ergebnis={ergebnisPo.conferenceChampionshipTeams}>
            <div className="frage__untertitel">AFC Championship</div>
            <div className="frage__grid frage__grid--2">
              {[1, 2].map(n => (
                <TeamAuswahl key={`afcc${n}`} label={`AFC Team ${n}`} value={po[`afcChampionship${n}`]} teams={AFC} disabled={gesperrt.playoff}
                  ausschliessen={[po[`afcChampionship${n === 1 ? 2 : 1}`]].filter(Boolean)}
                  onChange={v => setPlayoff(`afcChampionship${n}`, v)} />
              ))}
            </div>
            <div className="frage__untertitel">NFC Championship</div>
            <div className="frage__grid frage__grid--2">
              {[1, 2].map(n => (
                <TeamAuswahl key={`nfcc${n}`} label={`NFC Team ${n}`} value={po[`nfcChampionship${n}`]} teams={NFC} disabled={gesperrt.playoff}
                  ausschliessen={[po[`nfcChampionship${n === 1 ? 2 : 1}`]].filter(Boolean)}
                  onChange={v => setPlayoff(`nfcChampionship${n}`, v)} />
              ))}
            </div>
            {zeigeAndere.playoff && (
              <AndereListe andere={andere} laden={andereLaden} render={a =>
                [a.playoff.afcChampionship1, a.playoff.afcChampionship2].filter(Boolean).join(', ') + ' / ' +
                [a.playoff.nfcChampionship1, a.playoff.nfcChampionship2].filter(Boolean).join(', ')} />
            )}
          </Frage>
        )}

        {poCfg.superBowlTeams?.aktiv && (
          <Frage titel="🏆 Super Bowl Teams" punkte={`${poCfg.superBowlTeams.punkte} P je Team`} ergebnis={ergebnisPo.superBowlTeams}>
            <div className="frage__grid frage__grid--2">
              <TeamAuswahl label="AFC Champion" value={po.superBowlAFC} teams={AFC} disabled={gesperrt.playoff} onChange={v => setPlayoff('superBowlAFC', v)} />
              <TeamAuswahl label="NFC Champion" value={po.superBowlNFC} teams={NFC} disabled={gesperrt.playoff} onChange={v => setPlayoff('superBowlNFC', v)} />
            </div>
            {zeigeAndere.playoff && (
              <AndereListe andere={andere} laden={andereLaden} render={a => [a.playoff.superBowlAFC, a.playoff.superBowlNFC].filter(Boolean).join(' vs. ')} />
            )}
          </Frage>
        )}

        {(poCfg.superBowlWinner?.aktiv || poCfg.superBowlTeams?.aktiv) && (
          <Frage titel="👑 Super Bowl Sieger" punkte={poCfg.superBowlWinner?.aktiv ? `${poCfg.superBowlWinner.punkte} P` : 'Bonus'} ergebnis={ergebnisPo.superBowlWinner}>
            {po.superBowlAFC && po.superBowlNFC ? (
              <div className="frage__grid frage__grid--2">
                <TeamAuswahl label="Wer gewinnt?" value={po.superBowlWinner} teams={[po.superBowlAFC, po.superBowlNFC]} disabled={gesperrt.playoff}
                  onChange={v => setPlayoff('superBowlWinner', v)} />
              </div>
            ) : (
              <div className="andere-pick--leer">Erst beide Super-Bowl-Teams wählen.</div>
            )}
            {zeigeAndere.playoff && <AndereListe andere={andere} laden={andereLaden} logo render={a => a.playoff.superBowlWinner} />}
          </Frage>
        )}
      </section>

      {!saisonGesperrt && (!gesperrt.preSeason || !gesperrt.playoff) && (
        <div className="speichern-leiste">
          <span className="speichern-leiste__text">
            {hatAenderungen ? 'Änderungen noch nicht gespeichert' : 'Alles gespeichert'}
          </span>
          <button
            type="button"
            className={`speichern-leiste__button${hatAenderungen ? ' speichern-leiste__button--geaendert' : ''}`}
            onClick={handleSpeichern}
            disabled={speichernLaeuft || !hatAenderungen}
          >
            {speichernLaeuft ? '⏳ Speichere…' : '💾 Predictions speichern'}
          </button>
        </div>
      )}

      {toast && (
        <div className={`tipp-toast${toast.fehler ? ' tipp-toast--fehler' : ''}`} role="status">{toast.text}</div>
      )}
    </div>
  );
}

// ---------- Teilkomponenten ----------

function StatusChip({ gesperrt, deadline, aktiv }) {
  if (gesperrt) return <span className="bonus-block__status bonus-block__status--gesperrt">🔒 gesperrt</span>;
  if (!aktiv) return <span className="bonus-block__status bonus-block__status--bald">Deadlines aus</span>;
  if (!deadline) return <span className="bonus-block__status bonus-block__status--offen">offen</span>;
  const std = (new Date(deadline) - new Date()) / 3600000;
  if (std < 24) return <span className="bonus-block__status bonus-block__status--bald">⏰ noch {Math.max(1, Math.round(std))} h</span>;
  return <span className="bonus-block__status bonus-block__status--offen">offen</span>;
}

function Frage({ titel, punkte, ergebnis, children }) {
  let chip = null;
  if (ergebnis && ergebnis.von) {
    const q = ergebnis.richtig / ergebnis.von;
    const klasse = q >= 0.67 ? 'gut' : q > 0 ? 'mittel' : 'schlecht';
    chip = <span className={`frage__ergebnis frage__ergebnis--${klasse}`}>{ergebnis.richtig}/{ergebnis.von} richtig · {ergebnis.punkte} P</span>;
  }
  return (
    <div className="frage">
      <div className="frage__kopf">
        <h3>{titel}</h3>
        <span className="frage__punkte">{punkte}</span>
        {chip}
      </div>
      {children}
    </div>
  );
}

function AndereToggle({ aktiv, onToggle }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <button type="button" className={`chip${aktiv ? ' ist-aktiv' : ''}`} onClick={onToggle}>👥 Tipps der anderen</button>
    </div>
  );
}

function AndereListe({ andere, laden, render, logo = false }) {
  if (laden || andere === null) return <div className="andere-pick--leer">⏳ lade…</div>;
  const eintraege = andere.map(a => ({ name: a.name, wert: render(a) })).filter(e => e.wert);
  if (eintraege.length === 0) return <div className="andere-pick--leer">Niemand sonst hat hier getippt.</div>;
  return (
    <div className="andere-picks">
      {eintraege.map(e => (
        <span key={e.name} className="andere-pick">
          {logo && NFL_TEAMS[e.wert] && <img src={NFL_TEAMS[e.wert].logo} alt="" />}
          <span className="andere-pick__name">{e.name}</span>
          {e.wert}
        </span>
      ))}
    </div>
  );
}

export default BonusFragen;
